/*
 * Copyright (c) 2025 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, March 26th 2025, 8:56:18 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import FormLabel from "@mui/material/FormLabel";
import { useControlledInput } from "ui-widgets/forms";
import {
  APIFormInputErrorWrapper,
  CustomInputValidationErrorWrapper,
  HelperTextInputWrapper,
} from "ui-widgets/forms/FormInputWrappers";
import { GenericAPIMany2ManyFormInputProps } from "ui-widgets/forms/types";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { useAPIFetchList } from "lib/api";
import { PrimaryKeyValue } from "lib/types";

type RenderPropArgs<R> = {
  filteredItems: R[];
  selectedItemIds: PrimaryKeyValue<R>[];
  onCheckItem: (itemId: PrimaryKeyValue<R>) => void;
};

type RenderProp<R> = (args: RenderPropArgs<R>) => void;

export const APIMany2ManyFormInput = <P, R>({
  children,
  ...props
}: GenericAPIMany2ManyFormInputProps<P, R> & { children: RenderProp<R> }) => {
  const {
    field: { onChange, value },
  } = useControlledInput<P>(props);

  const selectedItemIds = (value ?? []) as PrimaryKeyValue<R>[];

  const OnChangeSelectedItemIds = (selectedItemIds: PrimaryKeyValue<R>[]) => onChange(selectedItemIds);

  const {
    items: apiItemList,
    isMakingAPIRequest,
    isAPIError,
    httpResponseStatusCode,
  } = useAPIFetchList<R>({ ...props, makeRequestOnInitialMount: true }); // Creating fetch list args from props since they are part of APIRelatedObjectSelectProps. This can probably be improved to dynamically destruct all fetchList args only without including irrelvant props as is done here.

  const onCheckItem = (itemId: PrimaryKeyValue<R>) => {
    if (selectedItemIds.includes(itemId)) {
      OnChangeSelectedItemIds(selectedItemIds.filter((v) => v !== itemId));
    } else {
      OnChangeSelectedItemIds([...selectedItemIds, itemId]);
    }
  };

  const filteredItems = props.getFilteredItems ? props.getFilteredItems(apiItemList) : apiItemList;

  return (
    <APIListProgressWrapper
      showLoader={isMakingAPIRequest}
      isAPIError={isAPIError}
      items={filteredItems}
      httpResponseStatusCode={httpResponseStatusCode}
      noDataMessage={props.noDataMessage}
    >
      <APIFormInputErrorWrapper {...props}>
        <CustomInputValidationErrorWrapper {...props}>
          <HelperTextInputWrapper helperText={props.helperText}>
            {props.label && <FormLabel component="h4">{props.label}</FormLabel>}

            {children?.({ filteredItems, selectedItemIds, onCheckItem })}
          </HelperTextInputWrapper>
        </CustomInputValidationErrorWrapper>
      </APIFormInputErrorWrapper>
    </APIListProgressWrapper>
  );
};
